import { Component, EventEmitter, Input, OnInit, Output, signal, Signal, WritableSignal } from '@angular/core';
import { ConfigInput, ConfigSelected } from '../../interfaces/interfaces';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FonpenComponent } from "../fonpen/fonpen.component";
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';
import { InputComponent } from "../input/input.component";
import { SelectedComponent } from "../selected/selected.component";
import { ProfessionComponent } from "../profession/profession.component";
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { maxYesterdayColombiaValidator, minSalaryValidator, MIN_SALARY } from 'app/config/functions.shared';

@Component({
  selector: 'app-pensioners',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    SelectedComponent,
    AutocompleteComponent
],
  templateUrl: './pensioners.component.html',
  styleUrl: './pensioners.component.css'
})
export class PensionersComponent implements OnInit{
  form: FormGroup;

  dataInputs: WritableSignal<ConfigInput[]> = signal([
    { label: 'Fecha de la resolución de pensión', subLabel: '', formName: 'resolution', placeholder: '', type: 'date', defaultValue: '', enabled: true, validation: [Validators.required, maxYesterdayColombiaValidator], textError: 'Por favor selecciona una fecha válida.' },
    { label: '¿Desde cuándo estás pensionado?', subLabel: '', formName: 'since', placeholder: '', type: 'date', defaultValue: '', enabled: true, validation: [Validators.required, maxYesterdayColombiaValidator], textError: 'Por favor selecciona una fecha válida.' },
        { label: '¿Cuál es tu ingreso por la actividad económica principal?', subLabel: '', formName: 'income', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [Validators.required, Validators.max(30000000), minSalaryValidator(MIN_SALARY)], textError: `El ingreso debe ser igual o superior a ${MIN_SALARY.toLocaleString()}` },
    { label: 'Otros Ingresos', subLabel: '', formName: 'incomeExtraActivity', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [], textError: 'Por favor ingresa un valor válido' },
  ]);

  dataSelected: WritableSignal<ConfigSelected[]> = signal([
    { label: '¿Cuál es tu nivel educativo?', subLabel: '', formName: 'academic', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor selecciona un nivel educativo', options: signal([]) },
    { label: '¿Perteneces a algún grupo de protección especial constitucional?', subLabel: '', formName: 'specialProtecionProgram', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor seleccione un elemento de la lista', options: signal([]) },
  ])

  @Output() formValid = new EventEmitter<any>();
  @Input() defaultData: any;

  pensionFunds: any[] = [];
  codpros: any[] = [];

  constructor(
    private _listService: ListsService,
  ){
    this.form = new FormGroup({
          fonpen: new FormControl(null, [Validators.required]),
          resolution: new FormControl(null, []),
          academic: new FormControl(null, [Validators.required]),
          specialProtecionProgram: new FormControl(null, [Validators.required]),
          since: new FormControl(null, [Validators.required]),
          codpro: new FormControl(null, [Validators.required]),
          income: new FormControl(null, [Validators.required, Validators.max(30000000)]),
          incomeExtraActivity: new FormControl(null, [Validators.required]),
        });

    this.form.valueChanges.subscribe(() => {
      this.emitFormState();
    });
  }

  ngOnInit(): void {
    this.getAllAcademicLevels();
    this.getAllSpecialProgram();
    this.getAllProfessions();

    this._listService.getPensioner().subscribe(res => {
      this.pensionFunds = res;
    });

    if (this.defaultData) {
      this.form.patchValue(this.defaultData)
      this.dataInputs.update((inputs:any) =>
        inputs.map((input:any) => ({
          ...input,
          defaultValue: this.defaultData[input.formName] ?? input.defaultValue
        }))
      );
      this.dataSelected.update((inputs:any) =>
        inputs.map((input:any) => ({
          ...input,
          defaultValue: this.defaultData[input.formName] ?? input.defaultValue
        }))
      );
    }
  }

  getAllProfessions() {
    this._listService.getCodpro().subscribe({
      next: (response: any) => {
        this.codpros = response;
      },
      error: (error: any) => {
        console.error('Error cargando profesiones:', error);
      }
    });
  }

  getAllSpecialProgram() {
    this._listService.getAllSpecialProgram().subscribe({
      next: (response: any) => {
        this.dataSelected().at(1)?.options.set(response);
      },
      error: (error: any) => {
        console.error('Error loading special programs:', error);
      }
    });
  }

  getAllAcademicLevels() {
    this._listService.getAllAcademicLevels().subscribe({
      next: (response: any) => {
        this.dataSelected().at(0)?.options.set(response);
      },
      error: (error: any) => {
        console.error('Error loading academic levels:', error);
      }
    });
  }

  setFonpen(fonpenId:number | null) {
      this.form.get('fonpen')!.setValue(fonpenId);
  }

  handleValue(event: { formName: string, value: string | number | null }) {
    const control = this.form.get(event.formName);
    control?.setValue(this.parseFieldValue(event.formName, event.value));
    control?.markAsTouched();
    control?.updateValueAndValidity();
    this.emitFormState();
  }

  private emitFormState(): void {
    this.formValid.emit(this.form.value);
  }

  private parseFieldValue(
    formName: string,
    value: string | number | null | undefined
  ): string | number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericFields = ['income', 'incomeExtraActivity'];
    if (!numericFields.includes(formName)) {
      return value;
    }

    const normalized = typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, ''));

    return Number.isNaN(normalized) ? null : normalized;
  }

  onProfessionSelected(selectedOption: any): void {
    this.form.get('codpro')!.setValue(selectedOption);
  }
}
